"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Crown, Loader2, LogOut, Pencil, Trash2, User, X } from "lucide-react";
import { signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import { SignInForm } from "@/components/auth/SignInForm";
import { LimitsIndicator } from "@/components/settings/LimitsIndicator";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { InfoTooltip } from "@/components/shared/InfoTooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { FadeImage } from "@/components/ui/fade-image";
import { Input } from "@/components/ui/input";
import { Link } from "@/i18n/navigation";
import { trackAccountDeleted } from "@/lib/analytics";
import { fadeVariants } from "@/lib/animations";
import { r2StoragePrefix, uploadToR2 } from "@/lib/r2-upload";
import { resizeProfilePhoto } from "@/lib/recipes/imageResize";
import { translateServerString } from "@/lib/server-strings/client";
import { clearStorage } from "@/lib/storage";

interface AccountSectionProps {
  onUpgradeClick: () => void;
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function AccountSection({ onUpgradeClick }: AccountSectionProps) {
  const t = useTranslations("settings");
  const tCommon = useTranslations("common");
  const {
    data: session,
    isLoading: isSessionLoading,
    isAuthenticated,
    isPremium,
    update: updateSession,
  } = useSession();
  const { isOffline } = useOnlineStatus();

  const [isMounted, setIsMounted] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [username, setUsername] = useState("");
  const [savedUsername, setSavedUsername] = useState("");
  const [usernameLoaded, setUsernameLoaded] = useState(false);
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || usernameLoaded) return;
    fetch("/api/account/profile")
      .then((res) => res.json())
      .then((data) => {
        if (data.username) {
          setUsername(data.username);
          setSavedUsername(data.username);
        }
        if (data.image) setProfileImage(data.image);
        setUsernameLoaded(true);
      })
      .catch(() => setUsernameLoaded(true));
  }, [isAuthenticated, usernameLoaded]);

  const isLoadingAuthentication = isSessionLoading && !isOffline;
  const isDeleteEnabled = deleteConfirmation === "DELETE";

  const displayImage = profileImage || session?.user?.image;

  const handleSignOut = () => {
    clearStorage();
    signOut({ callbackUrl: "/" });
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingPhoto(true);
    try {
      const { blob } = await resizeProfilePhoto(file);
      const pathname = `${r2StoragePrefix()}users/${cryptoRandomId()}.webp`;
      const publicUrl = await uploadToR2(pathname, blob, "image/webp");
      setProfileImage(publicUrl);

      await fetch("/api/account/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: publicUrl }),
      });

      await updateSession();
      toast.success(t("profileSaved"));
    } catch {
      toast.error(tCommon("internalServerError"));
    } finally {
      setIsUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleDeleteAccount = async () => {
    if (!isDeleteEnabled) return;

    setIsDeleting(true);

    if (session?.user?.id) {
      trackAccountDeleted({
        userId: session.user.id,
        email: session.user.email,
      });
    }

    try {
      const response = await fetch("/api/account/delete", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(translateServerString(data.error, { settings: t, common: tCommon }));
        return;
      }

      toast.success(translateServerString(data.message, { settings: t, common: tCommon }));
      clearStorage();

      setTimeout(() => {
        signOut({ callbackUrl: "/" });
      }, 1000);
    } catch (error) {
      console.error("Failed to delete account:", error);
      toast.error(t("failedToDeleteAccount"));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmation("");
    }
  };

  const handleSaveUsername = async () => {
    const trimmed = username.trim().toLowerCase();
    if (trimmed.length < 3) {
      setUsernameError(t("usernameTooShort"));
      return;
    }
    if (!/^[a-z0-9_]+$/.test(trimmed)) {
      setUsernameError(t("usernameInvalidFormat"));
      return;
    }

    setIsSavingUsername(true);
    setUsernameError("");

    try {
      const res = await fetch("/api/account/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: trimmed }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMsg = translateServerString(data.error, { settings: t, common: tCommon });
        setUsernameError(errorMsg);
        return;
      }

      setUsername(trimmed);
      setSavedUsername(trimmed);
      setIsEditingUsername(false);
      toast.success(t("profileSaved"));
    } catch {
      toast.error(tCommon("internalServerError"));
    } finally {
      setIsSavingUsername(false);
    }
  };

  return (
    <SettingsSection>
      <AnimatePresence mode="wait">
        {isLoadingAuthentication || !isMounted ? (
          <motion.div
            key="loading"
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-start gap-4">
              <div className="size-16 animate-pulse rounded-full bg-muted" />
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-5 w-32 animate-pulse rounded bg-muted" />
                <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                <div className="mt-2 h-4 w-24 animate-pulse rounded bg-muted" />
              </div>
            </div>
            <div className="border-t pt-4">
              <div className="space-y-2 rounded-lg border border-border p-4">
                <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                <div className="h-2 w-full animate-pulse rounded-full bg-muted" />
                <div className="h-4 w-56 animate-pulse rounded bg-muted" />
              </div>
            </div>
          </motion.div>
        ) : !isAuthenticated ? (
          <motion.div
            key="unauthenticated"
            className="space-y-4"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="space-y-2 text-center">
              <p className="text-sm text-muted-foreground">{t("signInDescription")}</p>
            </div>
            <SignInForm hideDisclaimer />
          </motion.div>
        ) : (
          <motion.div
            key="authenticated"
            className="space-y-4"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Avatar + Name + Premium */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={isUploadingPhoto || isOffline}
                className="group relative shrink-0 cursor-pointer rounded-full"
                aria-label={t("changeProfilePhoto")}
              >
                {isUploadingPhoto ? (
                  <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  </div>
                ) : displayImage && !isOffline ? (
                  <FadeImage
                    src={displayImage}
                    alt={session?.user.name || t("user")}
                    width={64}
                    height={64}
                    className="size-16 rounded-full object-cover transition-opacity group-hover:opacity-80"
                  />
                ) : (
                  <div className="flex size-16 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-muted/80">
                    <User className="size-8 text-muted-foreground" />
                  </div>
                )}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handlePhotoChange}
              />

              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div>
                    <h3 className="text-lg font-semibold">{session?.user.name}</h3>
                    <p className="text-sm text-muted-foreground">{session?.user.email}</p>
                  </div>

                  {isPremium ? (
                    <div className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
                      <Crown className="size-4 text-primary" />
                      <span>{t("premiumMember")}</span>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={onUpgradeClick}
                      disabled={isOffline}
                      className="w-fit shrink-0"
                    >
                      <Crown className="size-4 text-primary" />
                      {t("getPremium")}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Username */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center gap-1.5">
                <h3 className="font-medium">{t("username")}</h3>
                <InfoTooltip content={t("usernameHint")} />
              </div>

              {!usernameLoaded ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="h-5 w-48 animate-pulse rounded bg-muted" />
                  <div className="h-9 w-16 animate-pulse rounded bg-muted" />
                </div>
              ) : isEditingUsername ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value.toLowerCase());
                          setUsernameError("");
                        }}
                        placeholder={t("usernamePlaceholder")}
                        className="pe-9"
                        disabled={isSavingUsername || isOffline}
                        autoFocus
                      />
                      {username && !isSavingUsername && (
                        <button
                          type="button"
                          onClick={() => {
                            setUsername("");
                            setUsernameError("");
                          }}
                          className="absolute inset-y-0 end-2.5 flex items-center"
                          aria-label={tCommon("clear")}
                        >
                          <span className="flex size-5 items-center justify-center rounded-full bg-muted-foreground/30 text-background hover:bg-muted-foreground/50">
                            <X className="size-3.5" />
                          </span>
                        </button>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleSaveUsername}
                      disabled={isSavingUsername || isOffline || !username.trim()}
                    >
                      {isSavingUsername ? <Loader2 className="size-4 animate-spin" /> : t("save")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setUsername(savedUsername);
                        setUsernameError("");
                        setIsEditingUsername(false);
                      }}
                      disabled={isSavingUsername}
                    >
                      {tCommon("cancel")}
                    </Button>
                  </div>
                  {usernameError && <p className="text-xs text-destructive">{usernameError}</p>}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold">@{savedUsername}</p>
                    <Link
                      href={`/creators/${savedUsername}`}
                      className="block truncate text-sm text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                    >
                      nombox.app/creators/{savedUsername}
                    </Link>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditingUsername(true)}
                    disabled={isOffline}
                    className="shrink-0"
                  >
                    <Pencil className="size-3" aria-hidden />
                    {t("edit")}
                  </Button>
                </div>
              )}
            </div>

            {/* Limits */}
            <div className="border-t pt-4">
              <LimitsIndicator onUpgradeClick={onUpgradeClick} />
            </div>

            {/* Sign out / Delete */}
            <div className="pt-4">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={handleSignOut}
                  className="w-full sm:w-auto"
                  disabled={isOffline}
                >
                  <LogOut className="size-4" />
                  {t("signOut")}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  className="w-full sm:w-auto"
                  disabled={isOffline}
                >
                  <Trash2 className="size-4" />
                  {t("deleteAccount")}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteAccountTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteAccountDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col space-y-2">
            <label htmlFor="delete-confirmation" className="text-sm font-medium text-foreground">
              {t("deleteAccountConfirmLabel")}
            </label>
            <Input
              id="delete-confirmation"
              type="text"
              placeholder=""
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              disabled={isDeleting}
              className="font-mono"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{tCommon("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={!isDeleteEnabled || isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t("loading")}
                </>
              ) : (
                <>
                  <Trash2 className="size-4" />
                  {t("confirmDelete")}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
}
